import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { Settings, Users } from "lucide-react";

interface HomepageSettings {
  show_team_section: boolean;
}

const SiteSettingsManager = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: settings, isLoading } = useQuery({
    queryKey: ["site-settings", "homepage"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("value")
        .eq("id", "homepage")
        .single();

      if (error) throw error;
      return data.value as unknown as HomepageSettings;
    },
  });

  const updateSettingsMutation = useMutation({
    mutationFn: async (newSettings: HomepageSettings) => {
      const { error } = await supabase
        .from("site_settings")
        .update({ 
          value: JSON.parse(JSON.stringify(newSettings)),
          updated_at: new Date().toISOString()
        })
        .eq("id", "homepage");

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["site-settings"] });
      toast({
        title: "Settings updated",
        description: "Your homepage settings have been saved.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update settings. Please try again.",
        variant: "destructive",
      });
      console.error("Settings update error:", error);
    },
  });

  const handleToggleTeamSection = (checked: boolean) => {
    if (settings) {
      updateSettingsMutation.mutate({
        ...settings,
        show_team_section: checked,
      });
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-4 w-60" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Homepage Settings
        </CardTitle>
        <CardDescription>
          Control which sections appear on the main landing page
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between p-4 rounded-lg border border-border bg-muted/30">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-md bg-primary/10">
              <Users className="h-4 w-4 text-primary" />
            </div>
            <div>
              <Label htmlFor="team-section-toggle" className="text-base font-medium">
                Meet the Team Section
              </Label>
              <p className="text-sm text-muted-foreground">
                Show team members on the homepage
              </p>
            </div>
          </div>
          <Switch
            id="team-section-toggle"
            checked={settings?.show_team_section ?? true}
            onCheckedChange={handleToggleTeamSection}
            disabled={updateSettingsMutation.isPending}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default SiteSettingsManager;
